import React, { useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, AlertTriangle, ShieldAlert, Mail, Loader2 } from "lucide-react";

const REASON_LABEL = {
    empty: "Empty",
    below_point_count: "Too few points",
    below_bbox: "Too small (bbox)",
    below_duration: "Drawn too fast",
    below_inked_pixels: "Too few inked pixels",
    no_stroke_data: "No stroke data on record",
    image_decode_failed: "Image decode failed",
};

export default function AdminSignatureAuditPage() {
    const [report, setReport] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [applying, setApplying] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const runScan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/admin/diagnostics/signature-audit/scan", { dry_run: true });
            setReport(data);
            toast.success(`Scan complete — ${data.invalid_count} invalid of ${data.total_scanned} records`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setScanning(false);
        }
    };

    const applyInvalidations = async () => {
        setApplying(true);
        try {
            const { data } = await api.post("/admin/diagnostics/signature-audit/scan", { dry_run: false });
            setReport(data);
            toast.success(
                `Applied — invalidated ${data.actions.invalidated} signatures, queued ${data.actions.emails_queued} re-sign emails`
            );
            setConfirmOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setApplying(false);
        }
    };

    const invalidCount = report?.invalid_count ?? 0;
    const isDryRun = report?.dry_run === true;
    const affected = report?.affected_signer_count ?? 0;

    return (
        <DashboardShell>
            <div className="max-w-6xl mx-auto space-y-6">
                <header>
                    <h1 className="font-display text-3xl font-semibold text-slate-900">Signature Audit</h1>
                    <p className="text-sm text-slate-600 mt-1">
                        Scan every stored signature against the current strict validation rules. Dry-run is non-destructive.
                    </p>
                </header>

                <Card data-testid="sigaudit-run-card">
                    <CardHeader>
                        <CardTitle className="text-lg">Dry-run scan</CardTitle>
                        <CardDescription>
                            Walks submissions + contracts and reports invalid signatures. No DB writes, no emails.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            onClick={runScan}
                            disabled={scanning}
                            data-testid="sigaudit-run-btn"
                            className="bg-blue-900 hover:bg-blue-800 gap-2"
                        >
                            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                            {scanning ? "Scanning…" : "Run dry-run scan"}
                        </Button>
                    </CardContent>
                </Card>

                {report && (
                    <Card data-testid="sigaudit-report-card">
                        <CardHeader>
                            <CardTitle className="text-lg">Report</CardTitle>
                            <CardDescription>
                                {isDryRun ? "Dry-run results — nothing has been changed." : `Applied — run_id ${report.run_id}`}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <Stat label="Total scanned" value={report.total_scanned} testid="sigaudit-total" />
                                <Stat label="Invalid" value={invalidCount} accent={invalidCount > 0 ? "danger" : "neutral"} testid="sigaudit-invalid" />
                                <Stat label="Affected signers" value={affected} testid="sigaudit-affected" />
                                <Stat label="Emails queued" value={report.email_sent_count ?? 0} testid="sigaudit-emails" />
                            </div>

                            {report.by_reason && Object.keys(report.by_reason).length > 0 && (
                                <div data-testid="sigaudit-by-reason" className="rounded-md border border-slate-200 p-3">
                                    <div className="text-xs font-medium text-slate-600 mb-2">By reason</div>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(report.by_reason).map(([k, n]) => (
                                            <span key={k} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                                                {REASON_LABEL[k] || k}: <strong>{n}</strong>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {invalidCount > 0 && (
                                <div className="overflow-x-auto rounded-md border border-slate-200">
                                    <table className="min-w-full text-sm" data-testid="sigaudit-records-table">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr className="text-xs uppercase tracking-wider">
                                                <th className="px-3 py-2 text-left">Type</th>
                                                <th className="px-3 py-2 text-left">Record ID</th>
                                                <th className="px-3 py-2 text-left">Field</th>
                                                <th className="px-3 py-2 text-left">Signer email</th>
                                                <th className="px-3 py-2 text-left">Signed at</th>
                                                <th className="px-3 py-2 text-left">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {(report.invalid_records || []).slice(0, 200).map((r, i) => (
                                                <tr key={`${r.record_id}-${r.signature_id || i}`} data-testid={`sigaudit-row-${i}`}>
                                                    <td className="px-3 py-2 text-slate-700">{r.type}</td>
                                                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{(r.record_id || "").slice(0, 8)}…</td>
                                                    <td className="px-3 py-2 text-slate-700">{r.field_key || "—"}</td>
                                                    <td className="px-3 py-2 text-slate-700">{r.signer_email || "—"}</td>
                                                    <td className="px-3 py-2 text-slate-600 text-xs">{r.signed_at || "—"}</td>
                                                    <td className="px-3 py-2">
                                                        <span className="rounded-full bg-red-50 text-red-800 px-2 py-0.5 text-[11px] font-medium">
                                                            {REASON_LABEL[r.reason] || r.reason}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {invalidCount > 0 && isDryRun && (
                                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                                    <div className="flex items-start gap-3">
                                        <ShieldAlert size={18} className="text-red-700 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-red-900">Destructive action</div>
                                            <p className="text-xs text-red-800 mt-0.5">
                                                Applying will invalidate {invalidCount} signature(s) and email {affected} signer(s) asking them to re-sign.
                                            </p>
                                        </div>
                                        <Button
                                            variant="destructive"
                                            onClick={() => setConfirmOpen(true)}
                                            disabled={applying}
                                            data-testid="sigaudit-apply-btn"
                                            className="gap-2"
                                        >
                                            <Mail size={14} />
                                            Apply + send re-sign emails
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>

            <Dialog open={confirmOpen} onOpenChange={(v) => !applying && setConfirmOpen(v)}>
                <DialogContent data-testid="sigaudit-confirm-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle size={16} className="text-red-700" /> Confirm invalidation
                        </DialogTitle>
                        <DialogDescription>
                            This will invalidate <strong>{invalidCount}</strong> signature(s) and email <strong>{affected}</strong>{" "}
                            customer(s) to re-sign. This action is logged. Continue?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setConfirmOpen(false)}
                            disabled={applying}
                            data-testid="sigaudit-confirm-cancel"
                        >Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={applyInvalidations}
                            disabled={applying}
                            data-testid="sigaudit-confirm-apply"
                            className="gap-2"
                        >
                            {applying ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                            {applying ? "Applying…" : "Yes, invalidate + email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardShell>
    );
}

function Stat({ label, value, accent = "neutral", testid }) {
    const tone =
        accent === "danger"
            ? "border-red-200 bg-red-50 text-red-900"
            : "border-slate-200 bg-white text-slate-900";
    return (
        <div className={`rounded-md border ${tone} p-3`} data-testid={testid}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 text-2xl font-semibold">{value}</div>
        </div>
    );
}
